"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";

import { getNativeBridge } from "./useNativeApp";

export function MobileProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();

  // Register push token whenever session changes (login/logout)
  useEffect(() => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative || !session?.user) return;

    bridge.notifications
      .requestPermission()
      .then((granted: boolean) => {
        if (!granted) return null;
        return bridge.notifications.getToken();
      })
      .then((tokenData: { token: string; platform: string; deviceId: string } | null) => {
        if (!tokenData?.token) return;
        return fetch("/api/services/push-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(tokenData),
        });
      })
      .catch((err: unknown) => {
        console.warn("[MobileProvider] Push token registration failed:", err);
      });
  }, [session?.user?.id]);

  // Handle deep links on initial mount
  useEffect(() => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative) return;

    bridge.deepLink
      .getInitialLink()
      .then((url: string | null) => {
        if (!url) return;
        try {
          const appUrl = new URL(url);
          const target = appUrl.pathname + appUrl.search;
          if (typeof window !== "undefined" && window.location.pathname + window.location.search !== target) {
            window.location.href = target;
          }
        } catch {
          // ignore malformed URL
        }
      })
      .catch(() => {});
  }, []);

  // Update badge count periodically when in native app
  useEffect(() => {
    const bridge = getNativeBridge();
    if (!bridge?.isNative || !session?.user) return;

    const updateBadge = async () => {
      try {
        const res = await fetch("/api/services/unread-count");
        if (!res.ok) return;
        const { count } = await res.json();
        await bridge.notifications.setBadge(count || 0);
      } catch {
        // ignore
      }
    };

    updateBadge();
    const interval = setInterval(updateBadge, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [session?.user?.id]);

  return <>{children}</>;
}
